import { MERGE_STATES } from 'Features/SmartMerge/store';
import { useEffect, useImperativeHandle, useMemo } from 'react';

const useHighlight = ({
  message,
  messageId,
  getMessage,
  isMobile,
  expanded,
  mergeActive,
  mergeState,
  setNewHighlights,
  newHighlights,
  allHighlight,
  setAllHighlight,
  touchStartPos,
  setTouchStartPos,
  isTouching,
  setIsTouching,
  highlight,
  messageContainerRef,
  messages,
  ref,
}) => {
  const handleTextSelection = () => {
    if (!mergeActive || mergeState != MERGE_STATES.HIGHLIGHT || !highlight)
      return;

    if (isMobile && !expanded) {
      return;
    }

    document.documentElement.style.userSelect = '';
    document.documentElement.style.webkitUserSelect = '';
    document.documentElement.style.touchAction = '';

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const baseText = getMessage(message);
    if (typeof baseText !== 'string') return;

    const selectedText = selection.toString().trim();
    if (!selectedText) return;

    setTimeout(() => {
      selection.removeAllRanges();
      const lines = selectedText.split(/\r?\n/);
      proccessTextSelection(lines);
      setAllHighlight(false);
    }, 100);
  };

  const proccessTextSelection = (selectedText) => {
    const baseText = getMessage(message);
    if (typeof baseText !== 'string') return;

    const selection = [];
    for (let text of selectedText) {
      const { start, end } = getExpandedSelection(baseText, text);
      if (start === -1) continue;

      const textToHighlight = baseText.substring(start, end);
      selection.push({ start, end, text: textToHighlight });
    }
    handleSingleLineSelection(selection);
  };

  const getExpandedSelection = (baseText, selectedText) => {
    const linkMap = [];
    const linkRegex = /\[(.*?)\]\((.*?)\)/g;
    let matchLink;

    while ((matchLink = linkRegex.exec(baseText)) !== null) {
      linkMap.push({
        originalStart: matchLink.index,
        originalEnd: matchLink.index + matchLink[0].length,
        text: matchLink[1],
        url: matchLink[2],
        textStart: matchLink.index + 1,
        textEnd: matchLink.index + matchLink[1].length,
        fullLength: matchLink[0].length,
        textLength: matchLink[1].length,
      });
    }

    const cleanBaseText = baseText
      // .replace(/\[(.*?)\]\(.*?\)/g, '$1') // [text](url) -> text
      .replace(/\*\*/g, '') // Bold
      .replace(/\*/g, '') // Italic
      .replace(/\_\_/g, '') // Bold
      .replace(/\_/g, '') // Italic
      .replace(/\~\~/g, '') // Strikethrough
      .replace(/\`\`\`/g, '') // Code block
      .replace(/\`/g, '') // Inline code
      .replace(/\#/g, '') // Headers
      .replace(/\>/g, '') // Blockquotes
      .replace(/\|\|/g, ''); // Spoiler

    let cleanStart = cleanBaseText.indexOf(selectedText);
    if (cleanStart === -1) return { start: -1, end: -1 };

    let cleanEnd = cleanStart + selectedText.length;

    const urlRegex =
      /(https?:\/\/[^\s]+)|(www\.[^\s]+)|([^\s]+\.[a-zA-Z]{2,})/g;
    let match;
    let expandedForUrl = false;

    while ((match = urlRegex.exec(cleanBaseText)) !== null) {
      const urlStart = match.index;
      const urlEnd = urlStart + match[0].length;

      if (
        (urlStart >= cleanStart && urlStart < cleanEnd) ||
        (urlEnd > cleanStart && urlEnd <= cleanEnd) ||
        (urlStart <= cleanStart && urlEnd >= cleanEnd)
      ) {
        cleanStart = Math.min(cleanStart, urlStart);
        cleanEnd = Math.max(cleanEnd, urlEnd);
        expandedForUrl = true;
      }
    }

    let tempStart = cleanStart;
    while (
      tempStart > 0 &&
      !/[\s\n.,!?;:]/.test(cleanBaseText[tempStart - 1])
    ) {
      tempStart--;
    }

    let tempEnd = cleanEnd;
    while (
      tempEnd < cleanBaseText.length &&
      !/[\s\n.,!?;:]/.test(cleanBaseText[tempEnd])
    ) {
      tempEnd++;
    }

    if (expandedForUrl) {
      cleanStart = Math.min(tempStart, cleanStart);
      cleanEnd = Math.max(tempEnd, cleanEnd);
    } else {
      cleanStart = tempStart;
      cleanEnd = tempEnd;
    }

    const start = convertCleanPositionToOriginal(baseText, cleanStart);
    const end = convertCleanPositionToOriginal(baseText, cleanEnd);

    return { start, end };
  };

  const convertCleanPositionToOriginal = (originalText, cleanPosition) => {
    let markdownCount = 0;
    let cleanIndex = 0;
    let originalIndex = 0;

    while (cleanIndex < cleanPosition && originalIndex < originalText.length) {
      const markdownMatch = originalText
        .slice(originalIndex)
        .match(/\*\*|\*|__|_|~~|```|`|\#|\>/);
      if (markdownMatch && markdownMatch.index === 0) {
        markdownCount += markdownMatch[0].length;
        originalIndex += markdownMatch[0].length;
        continue;
      }

      cleanIndex++;
      originalIndex++;
    }

    return originalIndex;
  };

  const handleSingleLineSelection = (selection) => {
    let updatedHighlights = [...newHighlights];

    const allSelected = selection.every((sel) => {
      return updatedHighlights.some(
        (h) => sel.start >= h.start && sel.end <= h.end
      );
    });

    if (allSelected) {
      selection.forEach((sel) => {
        updatedHighlights = removeHighlight(
          updatedHighlights,
          updatedHighlights.find(
            (h) => sel.start >= h.start && sel.end <= h.end
          ),
          sel
        );
      });
    } else {
      const newSelections = selection.map((sel) => ({
        start: sel.start,
        end: sel.end,
        content: sel.text,
      }));

      updatedHighlights = mergeHighlights([
        ...updatedHighlights,
        ...newSelections,
      ]);
    }

    setNewHighlights(messageId, updatedHighlights);
  };

  const removeHighlight = (currentHighlights, existingHighlight, selection) => {
    const baseText = getMessage(message);
    return currentHighlights
      .map((h) => {
        if (h === existingHighlight) {
          if (selection.end === h.end) {
            return {
              start: h.start,
              end: selection.start,
              content: baseText.substring(h.start, selection.start),
            };
          }
          return {
            start: selection.end,
            end: h.end,
            content: baseText.substring(selection.end, h.end),
          };
        }
        return h;
      })
      .filter((h) => h.start < h.end);
  };

  const mergeHighlights = (highlights) => {
    if (highlights.length === 0) return [];

    const sorted = highlights.sort((a, b) => a.start - b.start);
    const merged = [];
    let current = { ...sorted[0] };

    for (let i = 1; i < sorted.length; i++) {
      const next = sorted[i];

      if (next.start <= current.end) {
        current.end = Math.max(current.end, next.end);
        current.content = getCurrentContent(
          getMessage(message),
          current.start,
          current.end
        );
      } else {
        merged.push(current);
        current = { ...next };
      }
    }

    merged.push(current);

    return merged;
  };

  const getCurrentContent = (baseText, start, end) => {
    return baseText.substring(start, end);
  };

  const processTextWithHighlights = (text, highlights) => {
    if (!highlights.length) return text;

    let result = text;
    const sortedHighlights = [...highlights].sort((a, b) => b.start - a.start);

    sortedHighlights.forEach(({ start, end }) => {
      const before = result.slice(0, start);
      const highlighted = result.slice(start, end);
      const after = result.slice(end);

      result =
        before +
        `<span class="bg-peachy-maroney/30">${highlighted}</span>` +
        after;
    });

    return result;
  };

  const highlightAll = () => {
    if (allHighlight) return;
    if (!messageContainerRef.current) return;

    const textContent = messageContainerRef.current.textContent;
    if (!textContent) return;

    const lines = textContent.split(/\r?\n/).filter((line) => line.trim());
    proccessTextSelection(lines);
    setAllHighlight(true);
  };

  const removeHighlights = () => {
    setNewHighlights(messageId, []);
    setAllHighlight(false);
  };

  const handleTouchStart = (e) => {
    if (!mergeActive || mergeState != MERGE_STATES.HIGHLIGHT || !highlight)
      return;
    setTouchStartPos({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    setIsTouching(true);
  };

  const handleTouchEnd = () => {
    if (!isTouching) return;
    setIsTouching(false);
    handleTextSelection();
  };

  const handleTouchMove = (e) => {
    if (!isTouching || !touchStartPos) return;

    const touch = e.touches[0];
    const moveThreshold = 10;

    const deltaX = Math.abs(touch.clientX - touchStartPos.x);
    const deltaY = Math.abs(touch.clientY - touchStartPos.y);

    if (deltaX > moveThreshold || deltaY > moveThreshold) {
      document.documentElement.style.userSelect = 'text';
      document.documentElement.style.webkitUserSelect = 'text';
      document.documentElement.style.touchAction = 'manipulation';
    }
  };

  const mobileSelectionAllowed = useMemo(() => {
    return isMobile && expanded;
  }, [isMobile, expanded]);

  useImperativeHandle(ref, () => ({
    highlightAll,
    removeHighlights,
  }));

  useEffect(() => {
    return () => {
      document.documentElement.style.userSelect = '';
      document.documentElement.style.webkitUserSelect = '';
      document.documentElement.style.touchAction = '';
    };
  }, []);

  useEffect(() => {
    if (messages.length == 0) {
      setAllHighlight(false);
    }
  }, [messages]);

  return {
    handleTouchEnd,
    handleTouchMove,
    handleTouchStart,
    handleTextSelection,
    mobileSelectionAllowed,
    processTextWithHighlights,
  };
};

export default useHighlight;
